import React from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { FaWhatsapp } from 'react-icons/fa';
import { formatXp, initials } from './format';
import { cutoutPlate, textHalo, tierGradient, utTier } from './tiers';
import { isCutout } from './profile/photo';
import { UT_LOGO } from './primitives';
import { TokenChips } from './TokenChips';

const WATERMARK_SIZES = new Set<UtCardSize>(['club', 'podium', 'podium-lead', 'fan', 'pack', 'feature']);
/** Token chips are 20px (design), 18px on the smallest cards. */
const SMALL_CHIP_SIZES = new Set<UtCardSize>(['mini', 'pitch', 'feature', 'pack']);

/** The minimum a card needs. The big number is always XP, never a rating. */
export interface UtCardPlayer {
  id: string;
  name: string;
  xp: number;
  rarity?: string | null;
  position?: string | null;
  /** In the WNF WhatsApp group: shows a subtle mark after "XP · POS". */
  whatsappMember?: boolean;
  /** Uploaded photo URL. Shown in a box between the XP and the name. */
  photo?: string | null;
}

export type UtCardSize = 'mini' | 'pitch' | 'podium' | 'podium-lead' | 'fan' | 'pack' | 'club' | 'feature';

interface SizeSpec {
  pad: string;
  xp: number;
  sub: number;
  subTrack: string;
  name: number;
  aspect: number;
  width?: number;
}

/** Type sizes per placement, taken from the prototype (docs/design/ut-v2). */
const SIZES: Record<UtCardSize, SizeSpec> = {
  mini: { pad: '12px 6px 14px', xp: 19, sub: 9, subTrack: '.1em', name: 13, aspect: 0.72 },
  pitch: { pad: '11px 6px 13px', xp: 18, sub: 9, subTrack: '.08em', name: 12, aspect: 0.74, width: 84 },
  podium: { pad: '16px 8px', xp: 22, sub: 10, subTrack: '.1em', name: 15, aspect: 0.72 },
  'podium-lead': { pad: '16px 8px', xp: 28, sub: 10, subTrack: '.1em', name: 15, aspect: 0.72 },
  fan: { pad: '16px 10px', xp: 26, sub: 11, subTrack: '.1em', name: 16, aspect: 0.72, width: 150 },
  pack: { pad: '12px 6px 14px', xp: 22, sub: 10, subTrack: '.1em', name: 14, aspect: 0.72 },
  club: { pad: '14px 8px 16px', xp: 22, sub: 10, subTrack: '.1em', name: 15, aspect: 0.72 },
  feature: { pad: '11px 6px 13px', xp: 18, sub: 9, subTrack: '.08em', name: 12, aspect: 0.72, width: 86 },
};

interface UtCardProps {
  player: UtCardPlayer;
  size?: UtCardSize;
  /** Show the tier name under the player name (team pack). */
  showTier?: boolean;
  /** Extra line under the name (Players grid: caps and win rate). */
  footer?: React.ReactNode;
  /** Defaults to the player's profile. Pass null for a non-interactive card. */
  to?: string | null;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  /** Mark the signed-in player's card with a small YOU tag (a ring would be clipped by the shield). */
  highlight?: boolean;
}

/**
 * Shield-shaped player card on the tier gradient: XP top-left, "XP · POS"
 * under it, name at the bottom. Taps through to the profile.
 */
export const UtCard: React.FC<UtCardProps> = ({ player, size = 'mini', showTier, footer, to, onClick, className, style, highlight }) => {
  const s = SIZES[size];
  const tier = utTier(player.rarity, player.xp);
  const pos = player.position ? ` · ${player.position}` : '';
  const cutout = isCutout(player.photo);
  const content = (
    <>
      {/* Faint club crest behind everything, like the profile card — only where the card is big
          enough for it to read as texture rather than noise. */}
      {WATERMARK_SIZES.has(size) && (
        <img
          src={UT_LOGO}
          alt=""
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/2 w-[78%] -translate-x-1/2 -translate-y-1/2 grayscale"
          style={{ opacity: 0.1 }}
        />
      )}
      {/* A kit-portrait cut-out stands on the card's bottom edge (the shield clips it), shoulders
          running off the bottom like a FIFA card; the name and footer sit on a plate over it. */}
      {cutout && (
        <>
          <img
            src={player.photo!}
            alt=""
            loading="lazy"
            draggable={false}
            className="pointer-events-none absolute bottom-0 left-1/2 h-[80%] w-auto max-w-none -translate-x-1/2"
          />
          <span className="pointer-events-none absolute inset-x-0 bottom-0 h-[40%]" style={{ background: cutoutPlate(tier) }} aria-hidden />
        </>
      )}
      {/* Top right: the YOU tag, then token badges (priority, shield, injury) with their detail on hover / tap. */}
      <span className="pointer-events-none absolute right-[9%] top-[10%] z-[2] flex flex-col items-end gap-[3px]">
        {highlight && (
          <span className="rounded-sm bg-[#0b0d12]/80 px-1 py-px font-ut-display text-[8px] font-bold tracking-[0.12em] text-[#e8c15a]">YOU</span>
        )}
        <TokenChips playerId={player.id} size={SMALL_CHIP_SIZES.has(size) ? 18 : 20} />
      </span>
      <span
        className="relative flex flex-col self-start pl-1 font-ut-display leading-none"
        style={{ ...(size === 'club' ? { paddingLeft: 4 } : {}), ...(cutout ? { textShadow: textHalo(tier) } : {}) }}
      >
        <span style={{ fontSize: s.xp, fontWeight: 800 }}>{formatXp(player.xp)}</span>
        <span style={{ fontSize: s.sub, fontWeight: 700, letterSpacing: s.subTrack }}>XP{pos}</span>
        {/* Own line: at the end of "XP · POS" it sat in a cut-out's hair (owner, 2 Oct). */}
        {player.whatsappMember && (
          <FaWhatsapp size={s.sub + 1} className="mt-0.5 opacity-50" aria-label="WhatsApp group member" title="WhatsApp group member" />
        )}
      </span>
      {/* Photo: its own slot between the XP and the name, so it fills the space left and never covers the text. */}
      {player.photo && !cutout && (
        <span className="relative my-[4%] min-h-0 w-[84%] flex-1 overflow-hidden rounded-md">
          <img
            src={player.photo}
            alt=""
            loading="lazy"
            draggable={false}
            className="pointer-events-none absolute inset-0 h-full w-full object-cover object-[50%_20%]"
          />
        </span>
      )}
      {/* Over a cut-out the text crosses the kit: a subtle halo in the tier colour keeps it readable. */}
      <span className="relative flex flex-col items-center gap-1" style={cutout ? { textShadow: textHalo(tier) } : undefined}>
        <span
          className="text-center font-ut-display font-bold uppercase"
          style={{ fontSize: s.name, lineHeight: 1.05, overflowWrap: 'anywhere' }}
        >
          {player.name}
        </span>
        {showTier && (
          <span className="font-ut-display font-bold uppercase opacity-85" style={{ fontSize: 9, letterSpacing: '.14em' }}>
            {tier.label}
          </span>
        )}
        {footer && (
          <span className={clsx('flex gap-1.5 font-ut-display', !cutout && 'opacity-90')} style={{ fontSize: 12 }}>
            {footer}
          </span>
        )}
      </span>
    </>
  );

  const cls = clsx(
    'ut-shield relative box-border flex flex-col items-center justify-between transition duration-150',
    (to !== null || onClick) && 'cursor-pointer hover:-translate-y-0.5 hover:brightness-[1.06]',
    className,
  );
  const cardStyle: React.CSSProperties = {
    aspectRatio: String(s.aspect),
    width: s.width,
    padding: s.pad,
    background: tierGradient(tier),
    color: tier.ink,
    ...style,
  };
  const label = `${player.name}, ${formatXp(player.xp)} XP${player.position ? `, ${player.position}` : ''}`;

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cls} style={cardStyle} aria-label={label}>
        {content}
      </button>
    );
  }
  if (to === null) {
    return (
      <div className={cls} style={cardStyle} aria-label={label}>
        {content}
      </div>
    );
  }
  return (
    <Link to={to ?? `/players/${player.id}`} className={cls} style={cardStyle} aria-label={label}>
      {content}
    </Link>
  );
};

/** A plain shield on the tier gradient with centred text (awards leader, rating target). */
export const UtShieldBadge: React.FC<{
  player: Pick<UtCardPlayer, 'name' | 'xp' | 'rarity'> & { id?: string };
  width?: number;
  label?: React.ReactNode;
  fontSize?: number;
  to?: string | null;
}> = ({ player, width = 70, label, fontSize = 18, to }) => {
  const tier = utTier(player.rarity, player.xp);
  const style: React.CSSProperties = {
    width,
    aspectRatio: '0.72',
    background: tierGradient(tier),
    color: tier.ink,
    fontSize,
  };
  const cls = 'ut-shield flex flex-none items-center justify-center font-ut-display font-extrabold';
  const text = label ?? initials(player.name);
  if (to === null || (!to && !player.id)) {
    return (
      <div className={cls} style={style}>
        {text}
      </div>
    );
  }
  return (
    <Link to={to ?? `/players/${player.id}`} className={clsx(cls, 'transition hover:brightness-[1.06]')} style={style} aria-label={player.name}>
      {text}
    </Link>
  );
};
